"use client";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  PlusCircle,
  Layout,
  Palette,
  Smartphone,
  Monitor,
  Tablet,
  ArrowRight,
  Sparkles,
} from "lucide-react";

interface OnboardingPageProps {
  onCreatePage: () => void;
  onBrowseTemplates: () => void;
}

export function OnboardingPage({
  onCreatePage,
  onBrowseTemplates,
}: OnboardingPageProps) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header Section */}
        <div className="mb-12 animate-fadeInUp text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4 text-center">
            Welcome to Web Builder
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto text-center">
            Create stunning pages for your store with our intuitive
            drag-and-drop builder. Start from scratch or choose from our
            professionally designed templates.
          </p>
        </div>

        {/* Features Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-fadeInUp"
          style={{ animationDelay: "0.2s" }}
        >
          {/* Visual Builder */}
          <div className="bg-card dark:bg-auth-card rounded-xl p-6 border border-border dark:border-auth-background shadow-sm">
            <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center mb-4">
              <Layout className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Visual Builder
            </h3>
            <p className="text-muted-foreground">
              Create stunning pages with our intuitive drag-and-drop interface.
            </p>
          </div>

          {/* Responsive Design */}
          <div className="bg-card dark:bg-auth-card rounded-xl p-6 border border-border dark:border-auth-background shadow-sm">
            <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center mb-4">
              <div className="flex items-center -space-x-1">
                <Smartphone className="w-3.5 h-3.5 text-primary" />
                <Tablet className="w-4.5 h-4.5 text-primary" />
                <Monitor className="w-5.5 h-5.5 text-primary" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Responsive Design
            </h3>
            <p className="text-muted-foreground">
              Your pages will look great on any device, automatically.
            </p>
          </div>

          {/* Custom Styling */}
          <div className="bg-card dark:bg-auth-card rounded-xl p-6 border border-border dark:border-auth-background shadow-sm">
            <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center mb-4">
              <Palette className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Custom Styling
            </h3>
            <p className="text-muted-foreground">
              Customize colors, fonts, and styles to match your brand.
            </p>
          </div>
        </div>

        {/* Action Cards */}
        <div
          className="bg-card dark:bg-auth-card rounded-xl p-8 border border-border dark:border-auth-background shadow-sm mb-8 animate-fadeInUp"
          style={{ animationDelay: "0.4s" }}
        >
          <h2 className="text-2xl font-semibold text-foreground mb-6">
            Ready to get started?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-left">
              <h3 className="text-lg font-medium text-foreground mb-2">
                Choose a Template
              </h3>
              <p className="text-muted-foreground mb-4">
                Browse our collection of professionally designed templates to
                get started quickly.
              </p>
              <Button
                variant="default"
                size="lg"
                onClick={onBrowseTemplates}
                className="w-full"
                leftIcon={<Layout className="w-5 h-5" />}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                Browse Templates
              </Button>
            </div>

            <div className="text-left">
              <h3 className="text-lg font-medium text-foreground mb-2">
                Start from Scratch
              </h3>
              <p className="text-muted-foreground mb-4">
                Create a blank page and build exactly what you envision with
                complete creative freedom.
              </p>
              <Button
                variant="outline"
                size="lg"
                onClick={onCreatePage}
                className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                leftIcon={<PlusCircle className="w-5 h-5" />}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                Create Blank Page
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
